import EditableField from '@wandb/weave/common/components/EditableField';
import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import {RunState} from '@wandb/weave/components/RunState';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {ReactElement, useState} from 'react';
import {Link} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import * as FilterTypes from '../../util/filterTypes';
import {getSweepDisplayName} from '../../util/getSweepDisplayName';
import * as Run from '../../util/runs';
import {getRunStateForRun} from '../../util/runs';
import * as RunTypes from '../../util/runTypes';
import {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import * as urls from '../../util/urls';
import {TimeDisplay} from '../elements/TimeDisplay';
import {useRunSelectorFeatureDisabled} from './RunSelectorFeaturesContext';
import {RunSelectorIssuesCell} from './RunSelectorIssuesCell';
import {RunSelectorTagCell} from './RunSelectorTagCell';
import type {WBTableColumn} from './WBTable/types';
import {WBTableCellCreatedAt} from './WBTable/WBTableCellCreatedAt';
import {WBTableCellDefault} from './WBTable/WBTableCellDefault';

type AddFilterFn = (
  key: RunTypes.Key,
  op: FilterTypes.ValueOp,
  value: RunTypes.Value
) => void;

type RenderCellProps = {
  addFilter?: AddFilterFn;
  column: WBTableColumn;
  entityName: string;
  isGroup: boolean;
  isHoveringRow: boolean;
  onSaveRowNotes: (newNotes: string) => void;
  projectName: string;
  row: WBTableRowWithRunSetInfo;
};

const CELL_FILTER_BLACKLIST: RunTypes.Key[] = [
  {section: 'run', name: 'github'},
  {section: 'run', name: 'runInfo.gpu'},
  {section: 'run', name: 'runInfo.gpuCount'},
];

export function RunSelectorCell({
  addFilter,
  column,
  entityName,
  isGroup,
  isHoveringRow,
  onSaveRowNotes,
  projectName,
  row,
}: RenderCellProps): ReactElement {
  const [isNotesTooltipOpen, setIsNotesTooltipOpen] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);

  const isDisabled = useRunSelectorFeatureDisabled();
  const isGroupRun = isGroup && row.group === row.name;
  const columnKey = column.key;
  const value = Run.getValue(row, columnKey);
  if (CELL_FILTER_BLACKLIST.some(f => Run.keysEqual(columnKey, f))) {
    return <WBTableCellDefault columnKey={columnKey} cellValue={value} />;
  }

  if (columnKey.section === 'tags') {
    return (
      <RunSelectorTagCell
        entityName={entityName}
        isGroup={isGroup}
        isGroupRun={isGroupRun}
        isHoveringRow={isHoveringRow}
        onAddFilter={addFilter}
        projectName={projectName}
        row={row}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'issues'})) {
    if (isGroup) {
      return <></>;
    }

    return (
      <RunSelectorIssuesCell
        row={row}
        entityName={entityName}
        projectName={projectName}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'state'})) {
    const state = getRunStateForRun(row);

    // For grouped runs with comma-separated states, display all state pills
    if (isGroup && state.includes(',')) {
      const stateValues = state.split(',').map(s => s.trim());
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={stateValues.join(', ')}
          addFilter={addFilter}>
          <TailwindContents>
            <div className="inline-flex gap-1 flex-nowrap whitepace-nowrap">
              {stateValues.map((stateValue: string) => (
                <div key={stateValue} className="inline-block">
                  <RunState value={stateValue} />
                </div>
              ))}
            </div>
          </TailwindContents>
        </WBTableCellDefault>
      );
    }

    // Single state display (default behavior for individual runs or groups with one state)
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={state}
        addFilter={addFilter}>
        <RunState value={state} />
      </WBTableCellDefault>
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'createdAt'})) {
    return (
      <WBTableCellCreatedAt createdAt={row.createdAt} addFilter={addFilter} />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'heartbeatAt'})) {
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={Run.formatTimestamp(row.heartbeatAt)}
        addFilter={addFilter}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'updatedAt'})) {
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={Run.formatTimestamp(row.updatedAt)}
        addFilter={addFilter}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'duration'})) {
    return (
      <TimeDisplay
        timestamp={new Date(row.createdAt)}
        format="month_round"
        now={new Date(row.heartbeatAt)}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'notes'})) {
    const isNotesReadOnly =
      (isGroup && !isGroupRun) || isDisabled('edit-run-notes');
    return (
      <Tooltip.Provider>
        <Tooltip.Root
          open={isNotesTooltipOpen && !isEditingNotes && row.notes.length > 0}
          onOpenChange={setIsNotesTooltipOpen}>
          <TailwindContents>
            <Tooltip.Trigger
              className={twMerge(
                'bg-none border-none p-0',
                isEditingNotes ? 'w-full' : 'max-w-full'
              )}>
              <EditableField
                className="runs-table-editable-field"
                data-test="add-notes-input"
                placeholder={isNotesReadOnly ? '-' : 'Add notes...'}
                readOnly={isNotesReadOnly}
                save={onSaveRowNotes}
                value={row.notes || ''}
                setIsEditing={isEditing => {
                  setIsEditingNotes(isEditing);
                  setIsNotesTooltipOpen(false); // prevents tooltip from opening right after an edit
                }}
              />
            </Tooltip.Trigger>
          </TailwindContents>
          <Tooltip.Portal>
            <Tooltip.Content className="max-w-[350px]">
              {row.notes}
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  }

  if (
    Run.keysEqual(columnKey, {
      section: 'run',
      name: 'sweep',
    })
  ) {
    const sweep = row.sweep;
    const sweepName = sweep?.name;
    if (!sweep || !sweepName) {
      return <>-</>;
    }

    if (isDisabled('runLinks')) {
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={getSweepDisplayName({...sweep, name: sweepName})}
          addFilter={addFilter}
        />
      );
    }

    return (
      <Link
        to={urls.sweep({
          entityName,
          projectName,
          sweepName: sweepName,
        })}>
        {getSweepDisplayName({...sweep, name: sweepName})}
      </Link>
    );
  }

  if (value != null) {
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={value}
        addFilter={addFilter}
      />
    );
  }

  return <>-</>;
}
